import { fetchClient } from "components/fetchClient";
import type { User } from "store/api/types";

type UserOrganization = {
  id: number;
  name: string;
};

export interface UserProfile extends Pick<User, "id" | "name" | "email"> {
  username: string;
  magicbell_key: string;
  organization: UserOrganization;
  is_supplier: boolean;
  is_tos_accepted: boolean;
  is_ifm: boolean;
  feature_flags?: Record<string, boolean>;
  featureFlags?: Record<string, boolean>;
}

export type ProfileSessionResult =
  | {
      kind: "authenticated";
      status: number;
      user: UserProfile;
    }
  | {
      kind: "unauthorized";
      status: 401;
      error: Error;
    }
  | {
      kind: "recoverable-error";
      errorKind: "http" | "network" | "parse";
      error: Error;
      status?: number;
      data?: unknown;
    };

type ProfileFetcher = (
  path: string,
  options?: RequestInit,
) => Promise<Response>;

export const PROFILE_PATH = "api/v1/profile/";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizeError = (error: unknown) =>
  error instanceof Error ? error : new Error("Unknown profile session error");

const isUserOrganization = (value: unknown): value is UserOrganization =>
  isRecord(value) &&
  typeof value.id === "number" &&
  typeof value.name === "string";

const isUserProfile = (value: unknown): value is UserProfile =>
  isRecord(value) &&
  typeof value.id === "number" &&
  typeof value.name === "string" &&
  typeof value.email === "string" &&
  typeof value.username === "string" &&
  typeof value.magicbell_key === "string" &&
  typeof value.is_supplier === "boolean" &&
  typeof value.is_tos_accepted === "boolean" &&
  typeof value.is_ifm === "boolean" &&
  isUserOrganization(value.organization);

const readJson = async (response: Response) => {
  try {
    return {
      data: await response.json(),
    };
  } catch (error) {
    return {
      error: normalizeError(error),
    };
  }
};

export async function loadUserProfile(
  profileFetcher: ProfileFetcher = fetchClient,
): Promise<ProfileSessionResult> {
  try {
    const response = await profileFetcher(PROFILE_PATH);
    const payload = await readJson(response);

    if (response.status === 401) {
      return {
        kind: "unauthorized",
        status: 401,
        error: new Error("401"),
      };
    }

    if (!response.ok) {
      return {
        kind: "recoverable-error",
        errorKind: "http",
        error: new Error(`HTTP error! status: ${response.status}`),
        status: response.status,
        data: payload.data,
      };
    }

    if (payload.error) {
      return {
        kind: "recoverable-error",
        errorKind: "parse",
        error: payload.error,
        status: response.status,
      };
    }

    if (!isUserProfile(payload.data)) {
      return {
        kind: "recoverable-error",
        errorKind: "parse",
        error: new Error("Invalid profile payload"),
        status: response.status,
        data: payload.data,
      };
    }

    return {
      kind: "authenticated",
      status: response.status,
      user: payload.data,
    };
  } catch (error) {
    const normalizedError = normalizeError(error);
    if (normalizedError.message === "401") {
      return {
        kind: "unauthorized",
        status: 401,
        error: normalizedError,
      };
    }

    return {
      kind: "recoverable-error",
      errorKind: "network",
      error: normalizedError,
    };
  }
}
